import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SpinnerGapIcon, WarningCircleIcon } from '@phosphor-icons/react';
import type { RootState, AppDispatch } from '../store/store';
import { clearCheckoutSession } from '../store/paymentSlice';
import { CallbackPage } from './CallbackPage';
import './PaymentPage.css';

const PAY_CONNECT_SCRIPT_SRC = 'https://banksapi.io/payconnect/app/js/app.js';
const PAY_CONNECT_SCRIPT_ID = 'pay-connect-js';

declare global {
  interface Window {
    payConnect?: {
      start: (
        options: {
          sessionId: string;
          providerId?: string;
          senderIban?: string;
        },
        onSuccess: (result: unknown) => void,
        onError: (error: unknown) => void
      ) => void;
    };
  }
}

/** Loads the PAY/Connect script once and resolves once it's ready to use. */
const loadPayConnectScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(
      PAY_CONNECT_SCRIPT_ID
    ) as HTMLScriptElement | null;
    if (existing) {
      if (window.payConnect) {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () =>
          reject(new Error('Failed to load PAY/Connect script'))
        );
      }
      return;
    }

    const script = document.createElement('script');
    script.id = PAY_CONNECT_SCRIPT_ID;
    script.src = PAY_CONNECT_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load PAY/Connect script'));
    document.body.appendChild(script);
  });
};

interface PaymentPageProps {
  // Present when BANKSapi's own top-level bank redirect (e.g. N26 SCA) lands
  // back on /payment after a full page reload instead of the in-page widget flow.
  sessionId?: string;
}

export const PaymentPage = ({
  sessionId: redirectSessionId,
}: PaymentPageProps = {}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const checkoutSession = useSelector(
    (state: RootState) => state.payment.checkoutSession
  );
  const [error, setError] = useState<string | null>(null);
  // Set once the widget calls onSuccess/onError — switches this page into status-polling mode
  // in place, without navigating away, so the user never leaves /payment.
  const [completed, setCompleted] = useState<{
    sessionId: string;
    transactionId?: string;
  } | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // Guard against double-initialization (e.g. React StrictMode double-invoke)
    if (started.current) return;

    // Resuming after the bank's own top-level SCA redirect (e.g. N26): per BANKSapi
    // support, re-calling start() with the same sessionId lets the widget read its
    // own localStorage and continue the flow where the user left off, instead of
    // restarting from bank search/login.
    if (redirectSessionId) {
      started.current = true;
      initWidget(redirectSessionId);
      return;
    }

    if (!checkoutSession) {
      navigate({ to: '/pay-rent' });
      return;
    }
    started.current = true;

    const { sessionId, transactionId } = checkoutSession;

    // transactionId is set from the backend's create-payment response — if it's missing here,
    // the callback page can never look up the transaction, so fail fast instead of navigating.
    if (!transactionId) {
      console.error(
        'PaymentPage: checkoutSession is missing transactionId',
        checkoutSession
      );
      dispatch(clearCheckoutSession());
      setError(t('paymentResult.invalidSession'));
      return;
    }

    initWidget(sessionId, transactionId);

    function initWidget(sessionId: string, transactionId?: string) {
      const onWidgetDone = () => {
        dispatch(clearCheckoutSession());
        setCompleted({ sessionId, transactionId });
      };

      loadPayConnectScript()
        .then(() => {
          if (!window.payConnect) {
            throw new Error('PAY/Connect widget failed to initialize');
          }
          window.payConnect.start({ sessionId }, onWidgetDone, onWidgetDone);
        })
        .catch((err: Error) => {
          setError(err.message);
        });
    }
    // Intentionally run once on mount — clearing checkoutSession on success/error
    // must not re-trigger this effect and race the status switch above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The widget finished (fresh start or resumed after a bank redirect) — verify
  // and render the final status right here, without ever leaving /payment.
  if (completed) {
    return (
      <CallbackPage
        paymentRequestId={completed.transactionId}
        sessionId={completed.sessionId}
      />
    );
  }

  return (
    <section className="mc-card mc-state payment-page">
      {error ? (
        <>
          <div className="mc-state__icon mc-state__icon--danger">
            <WarningCircleIcon size={46} weight="fill" />
          </div>
          <h2>{t('paymentResult.errorTitle')}</h2>
          <p>{error}</p>
          <div className="mc-actions">
            <Link to="/pay-rent" className="mc-btn mc-btn--primary">
              {t('paymentResult.tryAgain')}
            </Link>
          </div>
        </>
      ) : (
        <>
          <div className="mc-state__icon">
            <SpinnerGapIcon size={46} weight="bold" className="mc-spin" />
          </div>
          <p>{t('paymentResult.loadingForm')}</p>
        </>
      )}
      {/* PAY/Connect renders its overlay into this container */}
      <div id="pay-connect-app"></div>
    </section>
  );
};
